// Hook compartido: trae los ciclos de cultivo + estados fenológicos de un sector
// y los devuelve listos para usar como plotBands y plotLines en Highcharts.

import { useEffect, useState } from 'react';
import apiClient from '../requests/api/apiClient';
import { PHENOLOGY_PALETTE } from '../components/Highcharts/utils/colorUtils';
import { PHENOLOGY_BAND_CLASS } from '../constants/graphs';
import { escapeHtml } from '../utils/escapeHtml';

export interface PhenologyBand {
  name: string;
  from: number;
  to: number;
  color: string; // rgba(...) translúcido para el fondo
  solidColor: string; // rgb(...) sólido para borde / chip / línea
}

// Inyecta una sola vez el CSS de hover-reveal de bandas fenológicas en todos los Highcharts.
let cssInjected = false;
const ensurePhenologyCSS = () => {
  if (cssInjected || typeof document === 'undefined') return;
  cssInjected = true;
  const style = document.createElement('style');
  style.dataset.cdtecPhenology = '1';
  style.textContent = `
    .highcharts-plot-band.${PHENOLOGY_BAND_CLASS} {
      opacity: 0;
      transition: opacity 200ms ease;
      pointer-events: none;
    }
    .highcharts-container:hover .highcharts-plot-band.${PHENOLOGY_BAND_CLASS} {
      opacity: 1;
    }
  `;
  document.head.appendChild(style);
};

export const phenoCycleBandsToHighcharts = (bands: PhenologyBand[]) => {
  const plotBands = bands.map((b) => ({
    from: b.from,
    to: b.to,
    color: b.color,
    zIndex: 5,
    className: PHENOLOGY_BAND_CLASS,
    // En charts con arearange (ej. SoilMoistureChart), las series quedan siempre arriba
    // del plotBand. Esto fuerza que la banda fenológica se dibuje encima.
    inFront: true,
    label: {
      text: `<span style="background:${b.solidColor};color:#fff;padding:2px 6px;border-radius:4px;font-weight:800;font-size:10px;letter-spacing:0.02em;">${escapeHtml(b.name)}</span>`,
      align: 'left' as const,
      verticalAlign: 'top' as const,
      x: 6,
      y: 42,
      useHTML: true,
    },
  }));
  const plotLines = bands.flatMap((b, i) => {
    const lines: any[] = [{
      value: b.from,
      color: b.solidColor,
      width: 2,
      dashStyle: 'Solid',
      zIndex: 3,
    }];
    if (i === bands.length - 1) {
      lines.push({
        value: b.to,
        color: b.solidColor,
        width: 2,
        dashStyle: 'Dash',
        zIndex: 3,
      });
    }
    return lines;
  });
  return { plotBands, plotLines };
};

export const usePhenologicalCycleBands = (sectorId?: string | null): PhenologyBand[] => {
  const [bands, setBands] = useState<PhenologyBand[]>([]);

  useEffect(() => {
    ensurePhenologyCSS();
    let cancelled = false;
    if (!sectorId) {
      setBands([]);
      return () => { cancelled = true; };
    }
    const fetchCycles = async () => {
      try {
        const res: any = await (apiClient as any).cultivationCycleSector
          .cultivationCycleSectorDetail(sectorId);
        if (cancelled) return;
        const cycles: any[] = res?.data?.cultivationCycles || [];
        // Aplanar todos los estados de todos los ciclos y ordenarlos por fecha de inicio.
        // Esto matchea cómo el PhenologicalTimeline asigna colores: por orden global.
        const flat: Array<{ name: string; from: number; to: number }> = [];
        cycles.forEach((cycle: any) => {
          const states: any[] = cycle?.phenologicalStates || [];
          states.forEach((st: any) => {
            const fromT = new Date(st.startDate).getTime();
            const toT = new Date(st.endDate).getTime();
            if (!Number.isFinite(fromT) || !Number.isFinite(toT) || toT <= fromT) return;
            flat.push({ name: st.name || 'Estado', from: fromT, to: toT });
          });
        });
        flat.sort((a, b) => a.from - b.from);
        const result: PhenologyBand[] = flat.map((st, idx) => {
          const palette = PHENOLOGY_PALETTE[idx % PHENOLOGY_PALETTE.length];
          return {
            ...st,
            color: palette.fill,
            solidColor: palette.solid,
          };
        });
        setBands(result);
      } catch {
        if (!cancelled) setBands([]);
      }
    };
    fetchCycles();
    return () => { cancelled = true; };
  }, [sectorId]);

  return bands;
};

export default usePhenologicalCycleBands;
